import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShareNetwork, Copy, Tray } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { StatusBadge, PlatformChip } from "../lib/meta";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Explainer, WarmEmptyState } from "../components/friendly";

export default function Approvals() {
  const { activeClientId, user } = useApp();
  const canReview = ["owner", "admin", "operator"].includes(user?.role);
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [portalOpen, setPortalOpen] = useState(false);
  const [portal, setPortal] = useState(null);

  const openPortal = async () => {
    const { data } = await api.get(`/clients/${activeClientId}/portal`);
    setPortal(data);
    setPortalOpen(true);
  };

  const togglePortal = async (enabled) => {
    const { data } = await api.post(`/clients/${activeClientId}/portal`, { enabled });
    setPortal(data);
    toast.success(enabled ? "Client portal is live" : "Client portal disabled");
  };

  const portalUrl = portal?.portal_token ? `${window.location.origin}/portal/${portal.portal_token}` : "";

  const load = useCallback(() => {
    if (activeClientId) api.get(`/drafts?client_id=${activeClientId}&status=pending_approval,changes_requested`).then((r) => setDrafts(r.data));
  }, [activeClientId]);
  useEffect(load, [load]);

  const act = async (action) => {
    try {
      await api.post(`/drafts/${selected.draft_id}/${action}`, { comment });
      toast.success(`Draft ${action.replace("-", " ")}d`.replace("request-changesd", "sent back for changes"));
      setSelected(null); setComment(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="approvals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Approval Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">Review before anything reaches the Ready-to-Post queue.</p>
        </div>
        {canReview && (
          <Button data-testid="client-portal-button" variant="outline" className="rounded-lg gap-2" onClick={openPortal}>
            <ShareNetwork size={15} /> Client portal
          </Button>
        )}
      </div>
      <Explainer id="approvals">Every post stops here for a human yes before it can be posted. Approve it, request changes with a note, or reject it. You can also send clients a secure link so they approve without needing an account.</Explainer>

      <div className="bg-white border border-slate-200/80 rounded-xl card-float divide-y divide-slate-100">
        {drafts.map((d) => (
          <button key={d.draft_id} data-testid={`approval-row-${d.draft_id}`} onClick={() => { setSelected(d); setComment(""); }}
            className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50/70 transition-colors">
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-slate-800 truncate">{d.caption}</p>
              <div className="flex items-center gap-3 mt-1">
                <PlatformChip platform={d.platform} />
                {d.ai_generated && <span className="text-[10px] font-bold uppercase tracking-widest text-[hsl(9,55%,52%)] bg-[hsl(9,69%,62%)]/8 px-1.5 py-0.5 rounded" data-testid={`ai-badge-${d.draft_id}`}>AI-assisted</span>}
                {d.scheduled_date && <span className="text-[11px] text-slate-400">{d.scheduled_date}</span>}
                {d.safeguard_violations?.length > 0 && <span className="text-[11px] text-orange-600 font-medium">⚠ {d.safeguard_violations.length} safeguard flag(s)</span>}
              </div>
            </div>
            <StatusBadge status={d.status} />
          </button>
        ))}
        {!drafts.length && <div className="p-4"><WarmEmptyState icon={Tray} title="Inbox zero — nice." what="Nothing is waiting for review right now. When someone submits a draft from the Composer, it appears here for your yes before it can be posted." /></div>}
      </div>

      <Dialog open={portalOpen} onOpenChange={setPortalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Client approval portal</DialogTitle></DialogHeader>
          <p className="text-[13px] text-slate-500">Share a secure link with your client — they can approve or request changes on pending drafts. No account needed.</p>
          <label className="flex items-center justify-between border border-slate-200 rounded-xl p-3 text-[13px] font-medium">
            Portal enabled
            <Switch data-testid="portal-enabled-toggle" checked={portal?.portal_enabled ?? false} onCheckedChange={togglePortal} />
          </label>
          {portal?.portal_enabled && portalUrl && (
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
              <p data-testid="portal-url" className="text-[12px] font-mono truncate flex-1">{portalUrl}</p>
              <button data-testid="portal-copy-url" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Portal link copied"); }}
                className="text-slate-400 hover:text-slate-900 transition-colors"><Copy size={15} /></button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-display flex items-center gap-3">Review draft <PlatformChip platform={selected.platform} /></DialogTitle></DialogHeader>
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 text-[13px] whitespace-pre-wrap leading-relaxed" data-testid="review-caption">{selected.caption}</div>
              {selected.safeguard_violations?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-1">
                  {selected.safeguard_violations.map((v, i) => <p key={i} className="text-[12px] text-orange-700">⚠ {v.detail}</p>)}
                </div>
              )}
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-medium mb-2">Audit trail</p>
                <div className="space-y-1.5">
                  {selected.audit_trail?.map((a, i) => (
                    <p key={i} className="text-[12px] text-slate-500"><b className="text-slate-700">{a.by}</b> {a.action}{a.comment && <span className="italic"> — "{a.comment}"</span>} <span className="text-slate-300">· {new Date(a.at).toLocaleString()}</span></p>
                  ))}
                </div>
              </div>
              <Textarea data-testid="review-comment-input" rows={2} placeholder="Comment (required for changes/reject)" value={comment} onChange={(e) => setComment(e.target.value)} />
              {canReview ? (
              <div className="flex flex-wrap gap-2 justify-end">
                <Button data-testid="reject-button" variant="outline" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" onClick={() => act("reject")}>Reject</Button>
                <Button data-testid="request-changes-button" variant="outline" className="rounded-lg text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => act("request-changes")}>Request changes</Button>
                <Button data-testid="approve-button" className="rounded-lg bg-emerald-600 hover:bg-emerald-500" onClick={() => act("approve")}>Approve</Button>
              </div>
              ) : (
                <p className="text-[12px] text-slate-400 text-right">Your role ({user?.role}) can view but not review.</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
